import React, { useState, useEffect } from 'react';
import { Info, ChevronDown, AlertTriangle } from 'lucide-react';
import { getKpiSnapshotData, getAvailableYears } from '../services/kpiSnapshotApi';

/** Match dropdown mineral name to KPI mineral.name (e.g. "Rare Earth Elements" vs "Rare Earth"). */
function mineralMatches(filterMineral, mineralName) {
  if (!filterMineral || !mineralName) return false;
  const f = filterMineral.toLowerCase().trim();
  const m = String(mineralName).toLowerCase().trim();
  if (f === m) return true;
  if (m.includes(f) || f.includes(m)) return true;
  if (f.includes('rare') && m.includes('rare')) return true;
  return false;
}

function clampPercent(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return value;
  return Math.min(100, Math.abs(value));
}

const KPISnapshot = ({
  headerExtra = null,
  filterMineral = null,
  summarySlot = null,
  selectedYear: controlledYear = undefined,
  onSelectedYearChange = undefined,
}) => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [uncontrolledYear, setUncontrolledYear] = useState(null);
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const selectedYear = controlledYear !== undefined ? controlledYear : uncontrolledYear;
  const setSelectedYear = (year) => {
    if (controlledYear !== undefined) {
      if (typeof onSelectedYearChange === 'function') onSelectedYearChange(year);
      return;
    }
    setUncontrolledYear(year);
  };

  // Fetch available years on component mount
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const yearsResponse = await getAvailableYears();
        if (yearsResponse.success && yearsResponse.data && yearsResponse.data.length > 0) {
          setAvailableYears(yearsResponse.data);
          // Set first year as default if no year is selected
          if (!selectedYear) setSelectedYear(yearsResponse.data[0]);
        } else {
          // No years available from database
          setAvailableYears([]);
          if (!selectedYear) setSelectedYear(null);
        }
      } catch (err) {
        console.error('Error fetching available years:', err);
        // No fallback data - show error state
        setAvailableYears([]);
        if (!selectedYear) setSelectedYear(null);
      }
    };

    fetchYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch KPI data when selectedYear changes
  useEffect(() => {
    if (!selectedYear) {
      return;
    }

    const fetchKpiData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getKpiSnapshotData(selectedYear);
        if (response.success && response.data) {
          setKpiData(response.data);
        } else {
          setError(response.error || 'Failed to fetch KPI data');
          setKpiData(null);
        }
      } catch (err) {
        console.error('Error fetching KPI data:', err);
        setError(err.message);
        setKpiData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchKpiData();
  }, [selectedYear]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showYearDropdown && !event.target.closest('.year-dropdown')) {
        setShowYearDropdown(false);
      }
    };

    if (showYearDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showYearDropdown]);


  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Loading KPI Snapshot...</span>
        </div>
      </div>
    );
  }

  if (error || !kpiData) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4"/>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Unable to Load KPI Data</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error || 'No data available from the API'}
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }


  const handleYearChange = (year) => {
    setSelectedYear(year);
    setShowYearDropdown(false);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
      {/* Header with Year Selection */}
      <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Key Performance Indicators for Critical Minerals</p>
          {selectedYear && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Showing data for year: <span className="font-semibold">{selectedYear}</span>
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-2">
        {/* Year Selection Dropdown */}
        <div className="relative year-dropdown">
          <button
            onClick={() => !loading && setShowYearDropdown(!showYearDropdown)}
            disabled={loading}
            className={`flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors ${
              loading 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
            }`}
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {loading ? 'Loading...' : (selectedYear || (availableYears.length > 0 ? 'Select Year' : 'No Data'))}
            </span>
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            )}
          </button>
          
          {showYearDropdown && (
            <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10">
              {availableYears.length > 0 ? (
                availableYears.map((year) => (
                  <button
                    key={year}
                    onClick={() => handleYearChange(year)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg ${
                      year === selectedYear 
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {year}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No data available
                </div>
              )}
            </div>
          )}
        </div>
        {headerExtra}
        </div>
      </div>

      {/* Mineral Cards Grid (filter to one mineral when filterMineral is set) */}
      <div className="mb-6">
        {kpiData?.minerals && kpiData.minerals.length > 0 ? (
          (() => {
            const showAll = !filterMineral || filterMineral === 'All';
            const mineralsToShow = showAll
              ? kpiData.minerals
              : kpiData.minerals.filter((m) => mineralMatches(filterMineral, m.name));
            if (mineralsToShow.length === 0) {
              return (
                <div className="text-center py-8">
                  <Info className="h-8 w-8 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No KPI data for {filterMineral}</p>
                </div>
              );
            }
            return (
          <div className={`grid gap-4 ${mineralsToShow.length === 1 ? 'grid-cols-1 max-w-md' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
            {mineralsToShow.map((mineral, index) => (
              <div 
                key={mineral.name} 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {mineral.name}
                  </h3>
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-4">
                  {/* Import Dependency */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Import dependency</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-semibold text-white"
                      style={{
                        backgroundColor: mineral.riskLevel.toLowerCase() === 'high' ? '#011f4b' : 
                                       mineral.riskLevel.toLowerCase() === 'medium' ? '#03396c' : 
                                       mineral.riskLevel.toLowerCase() === 'low' ? '#b3cde0' : '#BF0001'
                      }}
                    >
                      {mineral.riskLevel}
                    </span>
                  </div>
                  
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    {typeof mineral.importDependency === 'number'
                      ? clampPercent(mineral.importDependency).toFixed(2)
                      : mineral.importDependency}%
                  </div>

                  {/* Top Partners */}
                  <div>
                    <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Top partners</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {mineral.topSupplier} {typeof mineral.topSharePct === 'number' ? mineral.topSharePct.toFixed(2) : mineral.topSharePct}% • {mineral.secondSupplier || 'Other'} {typeof mineral.secondSharePct === 'number' ? mineral.secondSharePct.toFixed(2) : mineral.secondSharePct || '0'}% • {mineral.thirdSupplier || 'Other'} {typeof mineral.thirdSharePct === 'number' ? mineral.thirdSharePct.toFixed(2) : mineral.thirdSharePct || '0'}%
                    </div>
                  </div>

                  {/* Reserves and Production */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">India Reserves</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {mineral.reservesIndiaT && mineral.reservesIndiaT > 0 ? (mineral.reservesIndiaT / 1000).toFixed(1) + ' kt' : '0.0'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">India Production</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {mineral.productionIndiaT && mineral.productionIndiaT > 0 ? (mineral.productionIndiaT / 1000).toFixed(1) + ' kt' : '0.0'}
                      </div>
                    </div>
                  </div>

                  {/* Import Growth and Price Trend */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Import growth</div>
                      <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {mineral.importGrowth ? `${mineral.importGrowth > 0 ? '+' : ''}${typeof mineral.importGrowth === 'number' ? mineral.importGrowth.toFixed(2) : mineral.importGrowth}%` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Price trend</div>
                      <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {mineral.priceTrend ? `${mineral.priceTrend > 0 ? '+' : ''}${typeof mineral.priceTrend === 'number' ? mineral.priceTrend.toFixed(2) : mineral.priceTrend}%` : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alert Box - Commented out for now */}
                {/* {mineral.alert && (
                  <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="space-y-2">
                      <div 
                        className="text-sm px-3 py-2 rounded-lg border text-gray-900 dark:text-gray-100"
                        style={{
                          borderColor: mineral.riskLevel?.toLowerCase() === 'high' ? '#dc2626' : 
                                       mineral.riskLevel?.toLowerCase() === 'medium' ? '#ea580c' : '#b3cde0',
                          backgroundColor: mineral.riskLevel?.toLowerCase() === 'high' ? '#fef2f2' : 
                                        mineral.riskLevel?.toLowerCase() === 'medium' ? '#fff7ed' : 'transparent'
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="font-medium">{mineral.alert}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )} */}
              </div>
            ))}
          </div>
            );
          })()
        ) : (
          <div className="text-center py-8">
            <Info className="h-8 w-8 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No mineral data available</p>
          </div>
        )}
      </div>

      {/* Executive Summary inside same box when single mineral is selected */}
      {filterMineral && summarySlot && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          {summarySlot}
        </div>
      )}

      {/* Footer */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-500">
          <span>Data source: UN Comtrade & India Ministry of Mines - {selectedYear ? `Year ${selectedYear}` : 'All years'}</span>
        </div>
        {kpiData?.aggregationMethod && (
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
            {kpiData.aggregationMethod}
          </div>
        )}
      </div>
    </div>
  );
};

export default KPISnapshot;
